/**
 * The ONE SSE reader for AI chat streams (btgpt `POST /btgpt`, builder
 * `GET /my-bridge/agents/build/jobs/:jobId/stream`). SSE over `fetch`, one
 * `data: <JSON>` frame per event, frames separated by a blank line; `:` lines
 * (`: ping`, every 15 s) are keep-alive comments. They carry no chunk, but they
 * prove the connection is alive, so the reader reports them (`onKeepAlive`)
 * instead of dropping them: "no byte for 35 s" is the one liveness signal the
 * browser owns (rev 3.3 §4). See rev33 "Event contract (as built)".
 */

/**
 * Every chunk a stream can carry. `text`/`thinking`/`tool_call`/`tool_done`/`phase`/`done`/`error`
 * are the shared contract (rev 3.3 §5, §10.3); the rest are btgpt-only extras passed through untouched.
 */
export interface AiStreamChunk {
  type: 'text' | 'thinking' | 'tool_call' | 'tool_done' | 'phase' | 'done' | 'error' | string;
  content?: string;
  /** `tool_call`: the raw tool name. */
  toolName?: string;
  /** `tool_call` / `tool_done`: the tool use id (a repeated `tool_call` with a known id adds its `input`). */
  toolUseId?: string;
  /** `tool_call`: the arguments as JSON, capped at 2 KB by the backend. */
  input?: string;
  /** `tool_done`: the tool reported an error. */
  isError?: boolean;
  /** `tool_done`: the result size (the content itself never leaves the backend). */
  bytes?: number;
  /** `thinking`: a summarized-thinking delta. */
  text?: string;
  /** `thinking`: the backend's running estimate of this thinking block's tokens (may come without `text`). */
  thinkingTokens?: number;
  /** `phase`: the backend step (`queued`, `claiming`, `no_warm_pod`, …, `ready`, `saving`). */
  phase?: string;
  /** `phase`: how long the step has been running when the chunk was sent (relative, skew-free). */
  sinceMs?: number;
  /** `phase`: the pod involved (Technical level only). */
  pod?: string;
  /** `phase`: a fixed backend string or a k8s reason, e.g. `retry 2: previous pod failed`. */
  detail?: string;
  error?: string;
  [extra: string]: unknown;
}

/** Parse one SSE frame (the text between two blank lines). Returns null for comments, empty or malformed frames. */
export function parseSseFrame(frame: string): AiStreamChunk | null {
  const data = frame
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).replace(/^ /, ''))
    .join('\n')
    .trim();
  if (!data) return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' && typeof parsed.type === 'string' ? (parsed as AiStreamChunk) : null;
  } catch {
    return null;
  }
}

/** True for a frame made only of `:` comment lines (the keep-alive `: ping`). */
export function isKeepAliveFrame(frame: string): boolean {
  const lines = frame.split(/\r?\n/).filter((line) => line !== '');
  return lines.length > 0 && lines.every((line) => line.startsWith(':'));
}

/**
 * Yield every chunk of an SSE response body, in order, until the body ends. Frames split
 * across reads are reassembled. A keep-alive comment frame yields nothing and calls
 * `onKeepAlive` (the connection is alive, nothing new to show).
 */
export async function* readSseChunks(body: ReadableStream<Uint8Array>, onKeepAlive?: () => void): AsyncGenerator<AiStreamChunk> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // Normalise the whole buffer: a \r\n can be split across two reads.
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');
      let at = buffer.indexOf('\n\n');
      while (at >= 0) {
        const frame = buffer.slice(0, at);
        buffer = buffer.slice(at + 2);
        const chunk = parseSseFrame(frame);
        if (chunk) yield chunk;
        else if (isKeepAliveFrame(frame)) onKeepAlive?.();
        at = buffer.indexOf('\n\n');
      }
    }
    buffer += decoder.decode();
    const tail = parseSseFrame(buffer);
    if (tail) yield tail;
  } finally {
    reader.releaseLock();
  }
}
